import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Gift, Loader2, Copy, Check, MousePointerClick, TrendingUp, DollarSign, Ticket, Trophy, Wallet, Twitter, Linkedin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-white/10 bg-slate-950 p-5" data-testid={testid}>
    <Icon className="mb-2 h-4 w-4 text-emerald-400" />
    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 font-mono text-2xl font-bold text-white">{value}</div>
  </div>
);

export default function Refer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [rewardCode, setRewardCode] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [wallet, setWallet] = useState(null);

  const link = ref ? `${window.location.origin}/?ref=${ref.code}` : "";
  const shareText = "I trade premium templates on Ticker Templates — grab 10% off your first one with my link:";
  const shareTargets = ref
    ? [
        { id: "x", label: "X", icon: Twitter, url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}` },
        { id: "linkedin", label: "LinkedIn", icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
        { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${link}`)}` },
      ]
    : [];

  const loadLeaders = () =>
    api.referralLeaderboard().then((r) => setLeaders(r.leaders || [])).catch((err) => console.error("Failed to load leaderboard", err?.message || err));

  const loadWallet = (addr) =>
    api.referralWallet(addr).then(setWallet).catch((err) => console.error("Failed to load wallet", err?.message || err));

  useEffect(() => { loadLeaders(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRewardCode(null);
    try {
      const r = await api.createReferral(email);
      let stats = r;
      try { stats = await api.referralStats(email); } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("stats not ready for fresh referral", err?.message || err); }
      setRef(stats);
      loadWallet(email);
    } catch {
      toast.error("Could not create your referral link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied", { description: "Share it anywhere to start earning." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const redeem = async () => {
    setRedeeming(true);
    try {
      const r = await api.redeemReferral(email);
      setRewardCode(r);
      try {
        localStorage.setItem("tt_reward_code", r.code);
        localStorage.setItem("tt_reward_amt", String(r.amount));
      } catch (err) { console.warn("Could not store reward for auto-apply", err?.message || err); }
      toast.success(`Reward redeemed — $${r.amount.toFixed(2)}`, { description: `$${r.amount.toFixed(2)} will auto-apply at your next checkout.` });
      try { setRef(await api.referralStats(email)); } catch (err) { console.error("Failed to refresh referral stats", err?.message || err); }
      loadWallet(email);
      loadLeaders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem your reward");
    } finally {
      setRedeeming(false);
    }
  };

  const balance = ref ? (ref.reward_earned ?? 0) : 0;

  return (
    <div className="grain min-h-screen bg-slate-900 text-white">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 rounded-full bg-emerald-500/8 blur-[120px]" />
      <header className="relative border-b border-white/10">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Ticker Templates" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Marketplace
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1000px] px-6 py-16">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Refer &amp; Earn</span>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
          Share the exchange, earn the spread
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-400">
          Give friends <span className="text-emerald-400">10% off</span> their first template. When they buy, you
          earn a <span className="text-emerald-400">10% reward</span> you can redeem as a discount code on your own
          next purchase — tracked live below.
        </p>

        <form onSubmit={generate} className="mt-8 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Gift className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="refer-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full border border-white/15 bg-slate-950 py-3.5 pl-11 pr-4 text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            data-testid="refer-generate-btn"
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-emerald-500 px-6 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            Get my link
          </button>
        </form>

        {ref && (
          <div className="mt-12" data-testid="refer-result">
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Your referral link</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                readOnly
                data-testid="refer-link"
                value={link}
                onClick={(e) => e.target.select()}
                className="flex-1 border border-emerald-500/30 bg-slate-950 px-4 py-3.5 font-mono text-sm text-emerald-300 outline-none"
              />
              <button
                data-testid="refer-copy-btn"
                onClick={copy}
                className="flex items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-emerald-400"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="mt-4" data-testid="share-buttons">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Share in one tap</span>
              <div className="mt-2 flex flex-wrap gap-3">
                {shareTargets.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`share-${s.id}`}
                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-slate-200 transition-colors duration-200 hover:border-emerald-400 hover:text-white"
                  >
                    <s.icon className="h-4 w-4" /> {s.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Stat icon={MousePointerClick} label="Clicks" value={ref.clicks ?? 0} testid="refer-stat-clicks" />
              <Stat icon={TrendingUp} label="Conversions" value={ref.conversions ?? 0} testid="refer-stat-conversions" />
              <Stat icon={DollarSign} label="Available reward" value={`$${balance.toFixed(2)}`} testid="refer-stat-earned" />
            </div>

            {/* Reward payout */}
            <div className="mt-6 border border-white/10 bg-slate-950 p-5" data-testid="reward-payout">
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-emerald-400">
                    <Ticket className="h-4 w-4" /> Redeem your rewards
                  </div>
                  <p className="mt-1 text-sm text-slate-400">
                    Convert your balance into a one-time discount code for your next checkout.
                  </p>
                </div>
                <button
                  data-testid="refer-redeem-btn"
                  onClick={redeem}
                  disabled={redeeming || balance < 1}
                  className="flex items-center justify-center gap-2 bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
                  {balance < 1 ? "Earn $1.00 to redeem" : `Redeem $${balance.toFixed(2)}`}
                </button>
              </div>
              {rewardCode && (
                <div
                  data-testid="reward-code-box"
                  className="mt-4 flex flex-col gap-1 border border-emerald-500/30 bg-emerald-500/10 p-4"
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-300/80">
                    Your discount code · ${rewardCode.amount.toFixed(2)} off
                  </span>
                  <span data-testid="reward-code-value" className="font-mono text-2xl font-bold tracking-widest text-emerald-300">
                    {rewardCode.code}
                  </span>
                  <span className="text-xs text-slate-400">
                    Automatically applied at your next checkout — no code to paste. (Code: {rewardCode.code})
                  </span>
                </div>
              )}
            </div>

            {/* Rewards Wallet */}
            {wallet && (
              <div className="mt-6 border border-white/10 bg-slate-950 p-5" data-testid="rewards-wallet">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-emerald-400">
                    <Wallet className="h-4 w-4" /> Rewards Wallet
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Available</div>
                      <div data-testid="wallet-available" className="font-mono text-lg font-bold text-emerald-400">${wallet.available.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Cashed out</div>
                      <div data-testid="wallet-redeemed" className="font-mono text-lg font-bold text-white">${wallet.redeemed_total.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
                {wallet.next_milestone && (
                  <div data-testid="milestone-progress" className="mb-4 flex items-center gap-2 border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
                    <Trophy className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
                    <span>
                      {wallet.next_milestone.remaining} more referral{wallet.next_milestone.remaining === 1 ? "" : "s"} to unlock a
                      <span className="font-bold text-emerald-400"> ${wallet.next_milestone.bonus.toFixed(2)} bonus</span> (at {wallet.next_milestone.at} conversions)
                    </span>
                  </div>
                )}
                {wallet.rewards.length === 0 ? (
                  <p data-testid="wallet-empty" className="text-sm text-slate-500">
                    No codes redeemed yet. Redeem your balance above to see your history here.
                  </p>
                ) : (
                  <div className="divide-y divide-white/5" data-testid="wallet-history">
                    {wallet.rewards.map((r) => (
                      <div key={r.code} data-testid={`wallet-code-${r.code}`} className="flex items-center justify-between py-2.5 text-sm">
                        <span className="font-mono tracking-widest text-emerald-300">{r.code}</span>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                          <span className="font-mono font-bold text-emerald-400">${r.amount.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Leaderboard */}
        {leaders.length > 0 && (
          <section className="mt-16" data-testid="referral-leaderboard">
            <div className="mb-5 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-emerald-400" />
              <h2 className="font-display text-2xl font-extrabold tracking-tight">Top Referrers</h2>
            </div>
            <div className="overflow-hidden border border-white/10">
              <table className="w-full text-left">
                <thead className="bg-slate-950 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Referrer</th>
                    <th className="px-4 py-3">Badge</th>
                    <th className="px-4 py-3 text-right">Conversions</th>
                    <th className="px-4 py-3 text-right">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.map((l) => (
                    <tr key={l.rank} data-testid={`leader-row-${l.rank}`} className="border-t border-white/5 text-sm text-slate-300">
                      <td className="px-4 py-3 font-mono text-emerald-400">{l.rank}</td>
                      <td className="px-4 py-3 font-mono">{l.referrer}</td>
                      <td className="px-4 py-3">
                        {l.badge && (
                          <span
                            data-testid={`leader-badge-${l.rank}`}
                            className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                              l.badge.tier === "gold"
                                ? "border-amber-400/50 bg-amber-400/10 text-amber-300"
                                : l.badge.tier === "silver"
                                ? "border-slate-300/40 bg-slate-300/10 text-slate-200"
                                : "border-orange-500/40 bg-orange-500/10 text-orange-300"
                            }`}
                          >
                            <Trophy className="h-3 w-3" /> {l.badge.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{l.conversions}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-400">${l.earned.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
